// =============================================================================
// SITUATION BRIEFING - Відображення поточної ситуації (Tactical HUD)
// =============================================================================
import { motion } from 'framer-motion';
import { useId, useState } from 'react';
import type { UrgencyLevel } from '../types';
import { ShareConsequencesModal } from './sharing/ShareConsequencesModal';

interface SituationBriefingProps {
  date: string;
  location: string;
  briefing: string;
  urgency: UrgencyLevel;
  consequences?: string;
  decisionNumber?: number;
}

function getUrgencyStyle(urgency: UrgencyLevel) {
  switch (urgency) {
    case 'CRITICAL':
      return {
        text: 'text-war-red',
        label: '! КРИТИЧНО !',
        bg: 'bg-war-red/10',
      };
    case 'HIGH':
      return {
        text: 'text-war-amber',
        label: '> ТЕРМІНОВО',
        bg: 'bg-war-amber/10',
      };
    default:
      return {
        text: 'text-war-primary',
        label: '# НОРМА',
        bg: 'bg-war-panel',
      };
  }
}

export function SituationBriefing({
  date,
  location,
  briefing,
  urgency,
  consequences,
  decisionNumber = 1,
}: SituationBriefingProps) {
  const style = getUrgencyStyle(urgency);
  const stableId = useId();
  const [shareModalOpen, setShareModalOpen] = useState(false);

  return (
    <>
      <section className="space-y-4" aria-label="Поточна ситуація">
        {/* Consequences - Previous choice feedback */}
        {consequences && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-war-primary/5 border-l-2 border-war-primary p-4 md:p-5"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-war-primary animate-pulse" />
                <span className="text-war-primary text-[10px] md:text-xs font-mono font-bold tracking-widest uppercase">
                  Аналіз наслідків:
                </span>
              </div>
              <button
                onClick={() => setShareModalOpen(true)}
                className="text-war-primary/60 hover:text-war-primary text-[10px] font-mono uppercase tracking-widest flex items-center gap-1 transition-colors"
                aria-label="Поділитися наслідками"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                  />
                </svg>
                <span className="hidden sm:inline">Поділитися</span>
              </button>
            </div>
            <p className="text-war-bright text-xs md:text-sm font-mono leading-relaxed opacity-90 italic">
              {consequences}
            </p>
          </motion.div>
        )}

        {/* Main Briefing Container */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-war-panel/80 border border-war-dim/20 shadow-[0_0_20px_rgba(0,0,0,0.5)]"
        >
          {/* Terminal Header */}
          <div className="flex justify-between items-center px-4 py-2 border-b border-war-dim/20 bg-war-bg/50">
            <div className="flex gap-4">
              <div className="flex flex-col">
                <span className="text-[9px] text-war-dim uppercase leading-none">
                  Дата
                </span>
                <span className="text-[11px] text-war-bright font-mono">
                  {date}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] text-war-dim uppercase leading-none">
                  Сектор
                </span>
                <span className="text-[11px] text-war-bright font-mono">
                  {location}
                </span>
              </div>
            </div>
            <div
              className={`${style.bg} ${style.text} px-3 py-1 text-[10px] font-mono font-bold border border-current opacity-80 uppercase tracking-tighter`}
            >
              {style.label}
            </div>
          </div>

          {/* Content Area */}
          <div className="p-5 md:p-8 relative min-h-[200px]">
            {/* Subtle grid background for content area */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:20px_20px]" />

            <pre className="relative font-mono text-war-bright text-sm md:text-base whitespace-pre-wrap leading-relaxed">
              {briefing}
              <motion.span
                animate={{ opacity: [1, 0] }}
                transition={{ repeat: Infinity, duration: 0.8 }}
                className="inline-block w-2 h-4 bg-war-primary ml-1 translate-y-0.5"
              />
            </pre>
          </div>

          {/* Footer info */}
          <div className="px-4 py-1.5 border-t border-war-dim/10 bg-war-bg/30 flex justify-between items-center">
            <span className="text-[8px] text-war-dim font-mono">
              ID: {stableId.replace(/:/g, '').substring(0, 7).toUpperCase()}
            </span>
            <span className="text-[8px] text-war-dim font-mono uppercase tracking-[0.2em]">
              End-of-Transmission
            </span>
          </div>
        </motion.div>
      </section>

      {/* Share Consequences Modal */}
      {consequences && (
        <ShareConsequencesModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          consequences={consequences}
          decisionNumber={decisionNumber}
        />
      )}
    </>
  );
}
